import { RootStore } from '../RootStore'
import { action, computed, observable } from 'mobx'
import { FilterPeriods } from '../ActionHistoryTableStore'
import { OnSelectCallbackParam } from 'react-daterange-picker'
import { ChartHeaderProps } from '../../components/charts'
import { Api, formatPrice, getStartAndEndDates, User } from '../../utilities'
import { ViewerEvents } from '../../pages/statistics/ViewersStats/ViewerStats.types'
import { GENERAL_STORE } from '../GeneralStore'
import moment from 'moment'
import { TotalViewerChartDTO, ViewerChartPeriod } from '../../components/charts/DualAxis/types'
import { STATISTICS_PAGE_STORE } from '../StatisticsPageStore'
import { OrderStatsRequestDTO, SalesDTO } from '../../models'
import { FilterDTO } from '../../models/FilterDTO'

export const STATISTICS_VIEWER_STORE = 'STATISTICS_VIEWER_STORE'

export class StatisticsViewerStore {
    private rootStore: RootStore

    constructor (rootStore: RootStore) {
      this.rootStore = rootStore
    }

    @observable periodFilter: FilterPeriods = FilterPeriods.PAST_3_MONTHS
    @observable customPeriod: OnSelectCallbackParam = null
    @observable viewerTotalStats: Map<String, ViewerChartPeriod> = new Map()
    @observable uniqueViewers = 0
    @observable uniqueProjects = 0
    @observable addExpenses = 0
    @observable revenue = 0
    @observable totalWatchTime = 0
    @observable totalVovWatchTime = 0
    @observable totalVovInteractionTime = 0
    @observable totalInteractionTime = 0
    @observable totalPageInteractionTime = 0

    @action init = () => {
      this.viewerTotalStats = new Map()
      this.uniqueViewers = 0
      this.uniqueProjects = 0
      this.addExpenses = 0
      this.totalWatchTime = 0
      this.totalVovWatchTime = 0
      this.totalVovInteractionTime = 0
      this.revenue = 0
      this.totalInteractionTime = 0
      this.totalPageInteractionTime = 0
    }

    @computed get period (): { startDate: number, endDate: number } {
      return getStartAndEndDates(this.customPeriod, this.periodFilter)
    }

    @action private setPeriodFilter = (periodFilter: FilterPeriods) => {
      this.periodFilter = periodFilter
    }

    @action private setCustomPeriod = (customPeriod: OnSelectCallbackParam) => {
      this.customPeriod = customPeriod
    }

    @action private setViewerTotalStats = (data : [TotalViewerChartDTO, Map<String, SalesDTO>]) => {
      const viewerData: Map<String, ViewerChartPeriod> = new Map()
      this.init()
      const [events, sales] = data as [TotalViewerChartDTO, Map<String, SalesDTO> ]
      this.dayLabelKeys.forEach((value) => {
        let dailyData = events.periodStats[value]
        if (!dailyData) {
          dailyData = {
            revenue: 0,
            totalWatchTime: 0,
            totalVovWatchTime: 0,
            totalVovInteractionTime: 0,
            totalInteractionTime: 0,
            totalPageInteractionTime: 0,
            totalOrders: 0
          }
        }
        dailyData.totalOrders = sales[value]?.total ?? 0
        // dailyData.revenue += sales[value]?.commission ?? 0
        viewerData.set(value, dailyData)
        this.totalWatchTime += dailyData.totalWatchTime
        this.totalVovWatchTime += dailyData.totalVovWatchTime
        this.addExpenses += dailyData.revenue
        this.revenue += dailyData.totalOrders
        this.totalInteractionTime += dailyData.totalInteractionTime
        this.totalPageInteractionTime += dailyData.totalPageInteractionTime
        this.totalVovInteractionTime += dailyData.totalVovInteractionTime
      })
      this.viewerTotalStats = viewerData
      this.uniqueProjects = events.uniqueProjects
      this.uniqueViewers = events.uniqueViewers
    }

    @computed get dayLabelKeys (): string[] {
      const daysCountForSelectedPeriod = moment(this.period.endDate).diff(moment(this.period.startDate), 'days')
      if (!daysCountForSelectedPeriod) return []

      const enumerateDaysBetweenDates = (startDate, endDate) => {
        startDate = moment(startDate);
        endDate = moment(endDate);

        let now = startDate, dates = [];

        while (now.isBefore(endDate) || now.isSame(endDate)) {
            dates.push(now.format('DD/MM/YYYY'));
            now.add(1, 'days');
        }
        return dates;
    };

      const dayLabelKeys = enumerateDaysBetweenDates(this.period.startDate, this.period.endDate)

      return dayLabelKeys
    }

    onPeriodFilterSelect = (periodFilter: FilterPeriods) : void => {
      this.setCustomPeriod(null)
      this.setPeriodFilter(periodFilter)
      this.onPeriodFilterChange()
    }

    onCustomPeriodSelect = (customPeriod: OnSelectCallbackParam) : void => {
      this.setPeriodFilter(FilterPeriods.CUSTOM)
      this.setCustomPeriod(customPeriod)
      this.onPeriodFilterChange()
    }

    private onPeriodFilterChange = (): void => {
      this.getViewersChartStats()
    }

    @computed get chartHeaderProps (): ChartHeaderProps {
      const projectsCount = this.uniqueProjects
      const total = 'Total Viewers: ' + this.uniqueViewers

      const totalWatchTimeLabel = moment.duration(this.totalWatchTime, 'milliseconds').format('hh:mm', { trim: false }) + 'h'
      const totalVovWatchTimeLabel = moment.duration(this.totalVovWatchTime, 'milliseconds').format('hh:mm', { trim: false }) + 'h'
      const totalVovInteractionTimeLabel = moment.duration(this.totalVovInteractionTime, 'milliseconds').format('hh:mm', { trim: false }) + 'h'
      const totalInteractionTimeLabel = moment.duration(this.totalInteractionTime, 'milliseconds').format('hh:mm', { trim: false }) + 'h'
      const totalPageInteractionTimeLabel = moment.duration(this.totalPageInteractionTime, 'milliseconds').format('hh:mm', { trim: false }) + 'h'
      const addExpensesLabel = formatPrice(this.addExpenses)
      const revenueLabel = formatPrice(this.revenue)

      const sections: Map<string, string | number > = new Map([
        [ViewerEvents.TOTAL_WATCH, totalWatchTimeLabel],
        [ViewerEvents.TOTAL_VOV_WATCH, totalVovWatchTimeLabel],
        [ViewerEvents.TOTAL_VOV_INTERACTION, totalVovInteractionTimeLabel],
        [ViewerEvents.TOTAL_INTERACTION, totalInteractionTimeLabel],
        [ViewerEvents.TOTAL_PAGE_INTERACTION_TIME, totalPageInteractionTimeLabel],
        [User.isShopOwner() ? ViewerEvents.ADD_EXPENSES : ViewerEvents.ADD_REVENUE, addExpensesLabel],
        [ViewerEvents.PURCHASE_REVENUE, revenueLabel]
      ])

      return {
        projectsCount,
        total,
        sections
      }
    }

    getViewersChartStats = (): void => {
      const {
        [GENERAL_STORE]: {
          handleApiError
        },
        [STATISTICS_PAGE_STORE]: {
          selectedStoreIds
        }
      } = this.rootStore

      Promise.all([
        Api.post<TotalViewerChartDTO, FilterDTO>(
          '/stats/summary/totalPeriod',
          {
            ...this.period,
            brandIds: selectedStoreIds
          },
          'statsApi'
        ),
        Api.post<Map<String, SalesDTO>, OrderStatsRequestDTO>(
          '/order/statistic/period',
          {
            startDate: moment(this.period.startDate != null ? this.period.startDate : moment()).format('YYYY-MM-DD'),
            endDate: moment(this.period.endDate != null ? this.period.endDate : moment()).format('YYYY-MM-DD'),
            brands: selectedStoreIds
          }
        )

      ]).then(this.setViewerTotalStats).catch(handleApiError)
    }
}
